import { ApprovalRequest } from "@/hooks/useApprovalSystem";
import { REQUEST_TYPE_CONFIG, STATUS_STYLES } from "./ApprovalRequestTypeConfig";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const fmt = (n: number) =>
new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

interface Props {
  request: ApprovalRequest;
  isSelected: boolean;
  onClick: () => void;
}

export const ApprovalListRow = ({ request, isSelected, onClick }: Props) => {
  const typeConfig = REQUEST_TYPE_CONFIG[request.request_type] ?? REQUEST_TYPE_CONFIG.other;
  const isCompleted = ["approved", "rejected", "withdrawn", "expired"].includes(request.status);

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left p-3 border-b transition-colors",
        isSelected && "bg-accent",
        isCompleted && "opacity-50"
      )}
    >
      <div className="flex items-start gap-2">
        <span className={cn("mt-1.5 h-2.5 w-2.5 rounded-full shrink-0", typeConfig.dotClass)} />
        <div className="flex-1 min-w-0">
          <p className={cn("text-[10px] uppercase tracking-wider font-semibold", typeConfig.color)}>
            {typeConfig.label}
          </p>
          <p className="text-sm truncate font-medium">{request.title}</p>
          <p className="text-xs truncate text-muted-foreground">
            {request.projects?.name ?? "—"}
            {request.request_data?.record_number ? ` · ${request.request_data.record_number}` : ""}
          </p>
          {request.linked_bill?.vendors?.name && (
            <p className="text-xs truncate text-muted-foreground">
              {request.linked_bill.vendors.name}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <div className="flex items-center gap-1">
            {request.urgency === "urgent" && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 animate-pulse">
                Urgent
              </Badge>
            )}
            <Badge className={cn("text-[10px] px-1.5 py-0", STATUS_STYLES[request.status])}>
              {request.status.replace("_", " ")}
            </Badge>
          </div>
          {request.linked_bill?.total != null && (
            <span className="font-semibold text-sm font-mono">
              {fmt(request.linked_bill.total)}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>
    </button>
  );
};